/**
 * PAGE PUBLIQUE : Formulaire d'ajout de coéquipier
 * Accessible sans authentification via /public/crews/:crewId/add-teammate
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { Button, PhoneInputIframe, SchoolAutocomplete } from '../components/ui';
import { isOver28AtEventDate } from '../lib/age-utils';
import { supabase } from '../lib/supabase';
import type { CrewPublicInfo, CreateCrewJoinRequestDto } from '../types/crew-join-request';
import { COUNTRIES } from '../types/school';

const AddTeammateSchema = z
  .object({
    last_name: z.string().min(1, 'Le nom est requis'),
    first_name: z.string().min(1, 'Le prénom est requis'),
    birth_date: z.string().min(1, 'La date de naissance est requise'),
    email: z.string().min(1, "L'email est requis").email('Email invalide'),
    phone: z.string().min(1, 'Le téléphone est requis'),
    is_not_student: z.boolean(),
    school: z.string().optional(),
    profession: z.string().optional(),
    address: z.string().min(1, "L'adresse est requise"),
    postal_code: z.string().min(1, 'Le code postal est requis'),
    city: z.string().min(1, 'La ville est requise'),
    country: z.string().min(1, 'Le pays est requis'),
  })
  .superRefine((data, ctx) => {
    if (!data.is_not_student && !data.school?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "L'école est requise",
        path: ['school'],
      });
    }
    if (data.is_not_student && !data.profession?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La profession est requise',
        path: ['profession'],
      });
    }
  });

type AddTeammateFormValues = z.infer<typeof AddTeammateSchema>;

export function PublicAddTeammateFormPage() {
  const { crewId } = useParams<{ crewId: string }>();

  const [crewInfo, setCrewInfo] = useState<CrewPublicInfo | null>(null);
  const [loadingCrew, setLoadingCrew] = useState(true);
  const [crewError, setCrewError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [dismissedAgeWarning, setDismissedAgeWarning] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AddTeammateFormValues>({
    resolver: zodResolver(AddTeammateSchema),
    defaultValues: {
      is_not_student: false,
      country: 'FRA',
    },
  });

  const isNotStudent = watch('is_not_student');
  const birthDateValue = watch('birth_date');

  const showAgeWarning =
    !dismissedAgeWarning &&
    !!birthDateValue &&
    isOver28AtEventDate(birthDateValue, crewInfo?.product_start_date ?? null);

  // Reset dismissed state quand la date change
  useEffect(() => {
    setDismissedAgeWarning(false);
  }, [birthDateValue]);

  useEffect(() => {
    if (!crewId) return;

    async function loadCrew() {
      setLoadingCrew(true);
      try {
        const { data, error } = await supabase.rpc('get_crew_public_info', {
          p_crew_id: crewId,
        });

        if (error) throw error;
        if (!data || data.length === 0) {
          setCrewError('Équipage introuvable.');
          return;
        }

        const row = data[0];
        setCrewInfo({
          id: row.id,
          name: row.name,
          crew_number: row.crew_number,
          member_count: Number(row.member_count),
          product_start_date: row.product_start_date,
          add_teammate_path: row.add_teammate_path ?? null,
          has_pending_request: row.has_pending_request ?? false,
        });
      } catch {
        setCrewError("Impossible de charger les informations de l'équipage.");
      } finally {
        setLoadingCrew(false);
      }
    }

    loadCrew();
  }, [crewId]);

  async function onSubmit(values: AddTeammateFormValues) {
    if (!crewId) return;

    const dto: CreateCrewJoinRequestDto = {
      crew_id: crewId,
      last_name: values.last_name,
      first_name: values.first_name,
      birth_date: values.birth_date,
      email: values.email,
      phone: values.phone,
      address: values.address,
      postal_code: values.postal_code,
      city: values.city,
      country: values.country,
      is_not_student: values.is_not_student,
      school: values.is_not_student ? undefined : values.school,
      profession: values.is_not_student ? values.profession : undefined,
    };

    const { error } = await supabase.from('crew_join_requests').insert(dto);

    if (error) {
      console.error('[PublicAddTeammateFormPage] submit error:', error);
      return;
    }

    setSubmitted(true);
  }

  // --- États de chargement / erreur ---

  if (loadingCrew) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (crewError || !crewInfo) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-zinc-200 p-6 text-center">
          <p className="text-zinc-600">{crewError || 'Équipage introuvable.'}</p>
        </div>
      </div>
    );
  }

  // --- Équipage complet ---

  if (crewInfo.member_count >= 2) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-zinc-200 p-8 text-center space-y-4">
          <div className="text-4xl">🏁</div>
          <h1 className="text-xl font-semibold text-zinc-900">Équipage au complet</h1>
          <p className="text-sm text-zinc-600">
            Cet équipage dispose déjà de deux membres et n'accepte plus de nouvelles candidatures.
          </p>
        </div>
      </div>
    );
  }

  // --- Demande déjà en attente ---

  if (crewInfo.has_pending_request) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-zinc-200 p-8 text-center space-y-4">
          <div className="text-4xl">⏳</div>
          <h1 className="text-xl font-semibold text-zinc-900">Demande en attente</h1>
          <p className="text-sm text-zinc-600">
            Une demande d'ajout est déjà en cours de traitement pour cet équipage. Notre équipe
            reviendra vers vous dans les plus brefs délais.
          </p>
        </div>
      </div>
    );
  }

  // --- Confirmation après soumission ---

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-zinc-200 p-8 text-center space-y-4">
          <div className="text-4xl">✅</div>
          <h1 className="text-xl font-semibold text-zinc-900">Demande reçue !</h1>
          <p className="text-sm text-zinc-600">
            Votre demande a bien été enregistrée. Notre équipe va la traiter dans les plus brefs
            délais et reviendra vers vous.
          </p>
          <p className="text-sm text-zinc-500">
            À très vite,
            <br />
            L'équipe du 4L Trophy
          </p>
        </div>
      </div>
    );
  }

  // --- Formulaire ---

  const crewLabel = crewInfo.crew_number
    ? `Équipage n°${crewInfo.crew_number}${crewInfo.name ? ` — ${crewInfo.name}` : ''}`
    : crewInfo.name || 'Équipage';

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* En-tête */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-zinc-900">Rejoindre un équipage</h1>
          <p className="mt-1 text-sm text-zinc-500">{crewLabel}</p>
        </div>

        <div className="bg-white rounded-lg border border-zinc-200 p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            {/* Informations personnelles */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide">
                Informations personnelles
              </h2>

              <div className="grid grid-cols-2 gap-3">
                {/* Nom */}
                <div>
                  <label
                    htmlFor="last_name"
                    className="block text-sm font-medium text-zinc-700 mb-1"
                  >
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="last_name"
                    type="text"
                    {...register('last_name')}
                    placeholder="Dupont"
                    className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-xs text-red-600">{errors.last_name.message}</p>
                  )}
                </div>

                {/* Prénom */}
                <div>
                  <label
                    htmlFor="first_name"
                    className="block text-sm font-medium text-zinc-700 mb-1"
                  >
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="first_name"
                    type="text"
                    {...register('first_name')}
                    placeholder="Marie"
                    className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-xs text-red-600">{errors.first_name.message}</p>
                  )}
                </div>
              </div>

              {/* Date de naissance */}
              <div className="relative">
                <label
                  htmlFor="birth_date"
                  className="block text-sm font-medium text-zinc-700 mb-1"
                >
                  Date de naissance <span className="text-red-500">*</span>
                </label>
                <input
                  id="birth_date"
                  type="date"
                  {...register('birth_date')}
                  className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
                {errors.birth_date && (
                  <p className="mt-1 text-xs text-red-600">{errors.birth_date.message}</p>
                )}

                {/* Popover dérogation âge — même design que PublicFormPage */}
                {showAgeWarning && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-orange-50 border border-orange-200 rounded-md shadow-md p-3 text-sm text-orange-800">
                    <button
                      type="button"
                      onClick={() => setDismissedAgeWarning(true)}
                      className="absolute top-2 right-2 text-orange-400 hover:text-orange-600"
                      aria-label="Fermer"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <p className="font-semibold mb-2 pr-5">
                      ⚠️ Information importante : Limite d'âge
                    </p>
                    <p className="mb-2">
                      L'aventure du 4L Trophy est{' '}
                      <strong>normalement réservée aux participants âgés de 18 à 28 ans</strong>.
                      Puisque vous avez plus de 28 ans,{' '}
                      <u>votre inscription nécessite une validation particulière</u>. Nous vous
                      invitons à contacter notre équipe à l'adresse contact@4ltrophy.com afin de
                      connaître la procédure et formuler une demande de dérogation.
                    </p>
                    <p className="mb-2">
                      Votre inscription pourra être validée à titre provisoire après le paiement,
                      mais ne deviendra définitive qu'après réception et validation de votre demande
                      de dérogation. En l'absence de demande, ou en cas de refus,{' '}
                      <strong>votre inscription pourra être annulée</strong>.
                    </p>
                    <p>
                      À très vite,
                      <br />
                      L'équipe du 4L Trophy
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Coordonnées */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide">
                Coordonnées
              </h2>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-zinc-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="marie.dupont@exemple.fr"
                  className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Téléphone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-zinc-700 mb-1">
                  Téléphone <span className="text-red-500">*</span>
                </label>
                <PhoneInputIframe
                  value={watch('phone') || ''}
                  onChange={(value) => setValue('phone', value, { shouldValidate: true })}
                  placeholder="6 12 34 56 78"
                />
                {errors.phone && (
                  <p className="mt-1 text-xs text-red-600">{errors.phone.message}</p>
                )}
              </div>
            </div>

            {/* Scolarité / Profession */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide">
                Situation
              </h2>

              {/* Checkbox étudiant */}
              <div className="flex items-center gap-2">
                <input
                  id="is_not_student"
                  type="checkbox"
                  {...register('is_not_student')}
                  className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                />
                <label htmlFor="is_not_student" className="cursor-pointer text-sm text-zinc-600">
                  Je ne suis plus étudiant(e)
                </label>
              </div>

              {/* École (visible si étudiant) */}
              {!isNotStudent && (
                <div>
                  <label htmlFor="school" className="block text-sm font-medium text-zinc-700 mb-1">
                    École <span className="text-red-500">*</span>
                  </label>
                  <SchoolAutocomplete
                    id="school"
                    value={watch('school') || ''}
                    onChange={(value) => setValue('school', value, { shouldValidate: true })}
                    hasError={!!errors.school}
                  />
                  {errors.school && (
                    <p className="mt-1 text-xs text-red-600">{errors.school.message}</p>
                  )}
                </div>
              )}

              {/* Profession (visible si non étudiant) */}
              {isNotStudent && (
                <div>
                  <label
                    htmlFor="profession"
                    className="block text-sm font-medium text-zinc-700 mb-1"
                  >
                    Profession <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="profession"
                    type="text"
                    {...register('profession')}
                    placeholder="Votre profession"
                    className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  {errors.profession && (
                    <p className="mt-1 text-xs text-red-600">{errors.profession.message}</p>
                  )}
                </div>
              )}
            </div>

            {/* Adresse */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide">
                Adresse
              </h2>

              {/* Adresse */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-zinc-700 mb-1">
                  Adresse <span className="text-red-500">*</span>
                </label>
                <input
                  id="address"
                  type="text"
                  {...register('address')}
                  placeholder="12 rue des Lilas"
                  className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
                {errors.address && (
                  <p className="mt-1 text-xs text-red-600">{errors.address.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Code postal */}
                <div>
                  <label
                    htmlFor="postal_code"
                    className="block text-sm font-medium text-zinc-700 mb-1"
                  >
                    Code postal <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="postal_code"
                    type="text"
                    {...register('postal_code')}
                    placeholder="75001"
                    className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  {errors.postal_code && (
                    <p className="mt-1 text-xs text-red-600">{errors.postal_code.message}</p>
                  )}
                </div>

                {/* Ville */}
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-zinc-700 mb-1">
                    Ville <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="city"
                    type="text"
                    {...register('city')}
                    placeholder="Paris"
                    className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  {errors.city && (
                    <p className="mt-1 text-xs text-red-600">{errors.city.message}</p>
                  )}
                </div>
              </div>

              {/* Pays */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-zinc-700 mb-1">
                  Pays <span className="text-red-500">*</span>
                </label>
                <select
                  id="country"
                  {...register('country')}
                  className="w-full h-9 px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white"
                >
                  <option value="">Sélectionnez un pays</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className="mt-1 text-xs text-red-600">{errors.country.message}</p>
                )}
              </div>
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                'Envoyer ma candidature'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
